import React, { useState } from "react";
import { Check, ChevronDown, ListTree } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const MODES = [
  ["total", "Kun totalsum", "Totalsum", "Kunden ser postene og én sum nederst."],
  ["delsummer", "Totalsum med delsummer", "Delsummer", "Hver jobb har sin sum. Underjobbene står under jobben, uten egen pris."],
  ["detaljert", "Delsummer og pris per underjobb", "Pris per underjobb", "Også hver underjobb viser sin pris."],
];

/** How prices appear to the customer – per quote, remembered for the customer's next one. Options and «etter regning» always keep their price. */
export function PriceModeMenu({ projectId, vm, customer, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cur = vm?.price_mode || "delsummer";
  const short = (MODES.find((m) => m[0] === cur) || MODES[1])[2];
  const set = async (m) => {
    if (m === cur || busy) return;
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/tilbud/prisvisning`, { mode: m });
      toast.success(`Prisvisning: ${MODES.find((x) => x[0] === m)[1]}${customer ? ` – foreslås også neste gang for ${customer}` : ""}`);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={busy} className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13.5px] font-medium text-foreground/75 outline-none transition-colors hover:bg-black/[0.05] hover:text-foreground data-[state=open]:bg-black/[0.06] disabled:opacity-50" title="Hvordan prisene vises for kunden" data-testid="tilbud-price-mode" data-value={cur}><ListTree className="h-3.5 w-3.5" /> <span className="max-sm:hidden">{short}</span><ChevronDown className="h-3.5 w-3.5 opacity-60" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={6} className="w-[320px] rounded-xl border-black/[0.06] p-1.5 shadow-float">
        <div className="px-2 pb-1.5 pt-1 text-[12px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Prisvisning i tilbudet</div>
        {MODES.map(([k, label, , hint]) => (
          <DropdownMenuItem key={k} onSelect={() => set(k)} className="cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2" data-testid={`tilbud-price-mode-${k}`}>
            <Check className={cn("mt-0.5 h-4 w-4 shrink-0", k === cur ? "text-[hsl(var(--navy))]" : "opacity-0")} strokeWidth={2.6} />
            <span className="min-w-0"><span className="block text-[13.5px] font-medium">{label}</span><span className="block text-[12px] leading-[1.4] text-muted-foreground">{hint}</span></span>
          </DropdownMenuItem>
        ))}
        <p className="px-2 pb-1 pt-2 text-[11.5px] leading-[1.45] text-muted-foreground" data-testid="tilbud-price-mode-note">{vm?.price_mode_from === "kunde" && customer ? `Valgt som sist for ${customer}. ` : ""}Opsjoner, enhetspriser og poster etter regning viser alltid sin pris.</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
